import React from 'react'

import { Text } from '@wartek-id/text-v1'

import type { PraktikKinerjaPendampinganFieldType } from 'types/kinerja-penilik-pegawai'

type List = Array<{
  title: string
  type: 'number' | 'bullet'
  hints: Array<string | React.ReactNode>
}>

export const PRAKTIK_KINERJA_HINTS: List = [
  {
    title: 'Pilih Praktik Kinerja berdasarkan:',
    type: 'bullet',
    hints: ['Paling mudah ditingkatkan', 'Paling banyak memberikan manfaat'],
  },
  {
    title: 'Pilih satuan pendidikan yang:',
    type: 'bullet',
    hints: [
      'Paling membutuhkan dukungan',
      'Kepala Sekolahnya dapat diajak bekerja sama',
      'Mengacu pada Laporan Perencanaan Pendampingan 4831',
    ],
  },
  {
    title: 'Tahap berikutnya:',
    type: 'number',
    hints: [
      'Ajukan Perencanaan',
      // eslint-disable-next-line react/jsx-key
      <div className="space-y-1">
        <Text>Pemeriksaan oleh Tim Kinerja dan Pejabat Penilai Kinerja</Text>
        <Text color="subdued" variant="body-sm">
          Lakukan revisi jika ada arahan
        </Text>
      </div>,
      'Persetujuan Perencanaan oleh Pejabat Penilai Kinerja',
      'Mulai tahap Pelaksanaan',
    ],
  },
]

export const SCORE_ENUM = {
  BAD: 'Kurang',
  FAIR: 'Sedang',
  GOOD: 'Baik',
}
export const SCORE_TRANSLATION = {
  [SCORE_ENUM.BAD]: 'Kurang',
  [SCORE_ENUM.FAIR]: 'Sedang',
  [SCORE_ENUM.GOOD]: 'Baik',
}
export const SCORE_COLORS = {
  [SCORE_ENUM.BAD]: 'critical',
  [SCORE_ENUM.FAIR]: 'warning',
  [SCORE_ENUM.GOOD]: 'success',
}
export const SCORE_BG_COLORS = {
  [SCORE_ENUM.BAD]: 'bg-red-50',
  [SCORE_ENUM.FAIR]: 'bg-orange-40',
  [SCORE_ENUM.GOOD]: 'bg-green-50',
}

export const PRAKTIK_KINERJA_MAX_SATUAN_PENDIDIKAN_SELECTION = 3

export const PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES: Record<
  PraktikKinerjaPendampinganFieldType,
  string
> = {
  pengalaman_mengesankan:
    '1. Sebutkan minimal 1 pengalaman yang paling mengesankan:',
  pengalaman_sulit: '2. Sebutkan minimal 1 pengalaman yang paling sulit:',
  pengalaman_memuaskan:
    '3. Sebutkan minimal 1 pengalaman yang paling memuaskan:',
  faktor_berhasil:
    '4. Sebutkan minimal 1 faktor yang membuat strategi berhasil:',
  faktor_terhambat:
    '5. Sebutkan minimal 1 faktor yang membuat strategi terhambat:',
  penyesuaian_sudah:
    '6. Sebutkan minimal 1 penyesuaian strategi yang sudah Anda lakukan:',
  penyesuaian_akan:
    '7. Sebutkan minimal 1 penyesuaian strategi yang akan Anda lakukan:',
  upaya: '8. Sebutkan minimal 3 upaya yang akan Anda lakukan:',
}

export const PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_FIELD_TYPES_STRUCTURE: {
  title: string
  fields: PraktikKinerjaPendampinganFieldType[]
}[] = [
  {
    title: 'Pengalaman selama melakukan strategi pendampingan',
    fields: [
      'pengalaman_mengesankan',
      'pengalaman_sulit',
      'pengalaman_memuaskan',
    ],
  },
  {
    title: 'Faktor yang membuat strategi pendampingan berhasil dan terhambat',
    fields: ['faktor_berhasil', 'faktor_terhambat'],
  },
  {
    title: 'Penyesuaian strategi yang sudah dilakukan',
    fields: ['penyesuaian_sudah'],
  },
  {
    title: 'Penyesuaian strategi yang perlu dilakukan nantinya',
    fields: ['penyesuaian_akan'],
  },
  {
    title: 'Upaya Tindak Lanjut yang akan Anda lakukan',
    fields: ['upaya'],
  },
]

export const PELAKSANAAN_PRAKTIK_KINERJA_PENDAMPINGAN_SUBMITTED_FIELD_TYPES: Record<
  PraktikKinerjaPendampinganFieldType,
  string
> = {
  pengalaman_mengesankan: 'Pengalaman yang paling mengesankan:',
  pengalaman_sulit: 'Pengalaman yang paling sulit:',
  pengalaman_memuaskan: 'Pengalaman yang paling memuaskan:',
  faktor_berhasil: 'Faktor yang membuat strategi berhasil:',
  faktor_terhambat: 'Faktor yang membuat strategi terhambat:',
  penyesuaian_sudah: 'Penyesuaian strategi yang sudah dilakukan:',
  penyesuaian_akan: 'Penyesuaian strategi yang akan dilakukan:',
  upaya: 'Upaya yang akan dilakukan:',
}

export const PELAKSANAAN_PRAKTIK_KINERJA_STAGES = {
  diskusi_persiapan: ['DISKUSI_PERSIAPAN'],
  pendampingan_sekolah: ['PENDAMPINGAN_SEKOLAH'],
  diskusi_tindak_lanjut: [
    'DISKUSI_TINDAK_LANJUT',
    'DISKUSI_TINDAK_LANJUT_PENILAIAN',
  ],
  upaya_tindak_lanjut: ['UPAYA_TINDAK_LANJUT'],
  refleksi_tindak_lanjut: ['REFLEKSI_TINDAK_LANJUT'],
  penilaian: ['REFLEKSI_TINDAK_LANJUT_PENILAIAN', 'SELESAI'],
}

export const PELAKSANAAN_PRAKTIK_KINERJA_STATE_TITLE = {
  diskusi_persiapan: 'Diskusi Persiapan',
  pendampingan_sekolah: 'Pendampingan Satuan Pendidikan',
  diskusi_tindak_lanjut: 'Diskusi Tindak Lanjut',
  upaya_tindak_lanjut: 'Upaya Tindak Lanjut',
  refleksi_tindak_lanjut: 'Refleksi Tindak Lanjut',
}

export const MSG_ERR_SUBMIT = 'Gagal kirim. Coba lagi.'
export const MSG_ERR_SAVE_DRAFT = 'Gagal simpan draf. Coba lagi.'
export const MSG_SUCCESS_SUBMIT = 'Berhasil kirim!'
export const MSG_SUCCESS_SAVE_DRAFT = 'Berhasil simpan draf!'
